import { IItem } from '../../../../models/Items/IItem';
import TableCell from '@mui/material/TableCell';
import priceFormatter from '../../../../common/priceFormatter';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import getTitle from '../../../../common/translations';
import { getCommonStyles } from '../../../../common/commonStyles';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../../context/LanguageContext';
import { DTO_STATE, FIELDS, IS_DELETED } from '../../../../common/dictionary';
import SendIcon from '@mui/icons-material/Send';
import { getStyles, HeadCell } from '../../tableCommon';
import { itemAPI } from '../../../../services/ItemServices';
import { IStock } from '../../../../models/Items/IStock';
import MySnackBar from '../../../common/snackBars/MySnackBar';
import DeleteIcon from '@mui/icons-material/Delete';
import { DeleteDialog } from './DeleteDialog';

interface ItemCardProps {
    item: IItem;
    columnInfo: HeadCell[];
}

function ItemCard({ item, columnInfo }: ItemCardProps) {
    const { language } = useLanguage();
    const inputCountRef = useRef<HTMLInputElement>(null);
    const inputPriceRef = useRef<HTMLInputElement>(null);
    const inputWOPRef = useRef<HTMLInputElement>(null);
    const buttonStyle = { lineHeight: '1.25rem' /* fontSize: '0.75rem' */ };
    const { simpleBtnStyle } = getCommonStyles();
    const { buttonItemsStyle, updateFormInput } = getStyles();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [modifiedItem, setModifiedItem] = useState<IItem>({ ...item });
    const [disabledSaveButton, setDisabledSaveButton] = useState<boolean>(false);
    const [saveItemSuccessSnackBar, setSaveItemSuccessSnackBar] = useState<boolean>(false);
    const [saveItem, { error: saveItemError, isSuccess: saveItemIsSuccess }] = itemAPI.useSaveItemMutation();

    const [newStock, setNewStock] = useState<IStock>({
        productionCompany: '',
        itemId: item.id || -1,
        count: 0,
        price: 0,
        weightOfPiece: item.weightOfPiece || 0,
        isDeleted: IS_DELETED.ACTIVE,
        state: DTO_STATE.DTO_NEW,
        createdAt: null,
    });

    const handelDialogOpen = () => {
        setDialogOpen(!dialogOpen);
    };

    const handleChange = (e: any) => {
        if (e.target.id === 'name') {
            setModifiedItem((prevState) => {
                return {
                    ...prevState,
                    [e.target.id]: e.target.value,
                };
            });
        } else {
            setNewStock((prevState) => {
                return {
                    ...prevState,
                    [e.target.id]: Number(e.target.value),
                };
            });
        }
    };

    const handleSaveItem = () => {
        setDisabledSaveButton(false);
        saveItem(modifiedItem).then((res) => {
            if ('data' in res) {
                setSaveItemSuccessSnackBar(true);
                setDialogOpen(false);
            }
        });
    };

    const handleDeleteItemDialog = () => {
        setDeleteDialogOpen(!deleteDialogOpen);
    };

    const deleteItem = () => {
        setModifiedItem((prevState) => {
            return {
                ...prevState,
                state: DTO_STATE.DTO_DELETED,
            };
        });
    };

    useEffect(() => {
        if (modifiedItem.state === DTO_STATE.DTO_DELETED) {
            saveItem(modifiedItem).then((res) => {
                if ('data' in res) {
                    setSaveItemSuccessSnackBar(true);
                    setDeleteDialogOpen(false);
                    setDialogOpen(false);
                }
            });
        }
    }, [modifiedItem]);

    useEffect(() => {
        if (dialogOpen && modifiedItem.state !== DTO_STATE.DTO_DELETED) {
            const modifiedName = item.name !== modifiedItem.name;
            const correctValues =
                newStock.count !== undefined &&
                newStock.count > 0 &&
                newStock.price !== undefined &&
                newStock.price > 0 &&
                newStock.weightOfPiece !== undefined &&
                newStock.weightOfPiece > 0;

            setDisabledSaveButton(modifiedName || correctValues);

            setModifiedItem((prevState) => {
                return {
                    ...prevState,
                    state: DTO_STATE.DTO_MODIFIED,
                    stocks: [{ ...newStock }],
                };
            });
        }
    }, [newStock, modifiedItem]);

    return (
        <Fragment>
            <TableCell id={item.name}>
                <IconButton aria-label="expand item" size="small" onClick={handelDialogOpen}>
                    <SettingsIcon />
                </IconButton>
                {item.name}
            </TableCell>
            {columnInfo.find((x) => x.id === 'categories')?.visible && (item.categories || item.categories === 0 || item.categories === '0') && (
                <TableCell align="center">{'-'}</TableCell>
            )}
            {(item.count || item.count === 0) && <TableCell align="center">{item.count.toString()}</TableCell>}
            {columnInfo.find((x) => x.id === 'lastPrice')?.visible && (item.lastPrice || item.lastPrice === 0) && (
                <TableCell align="center">{priceFormatter(item.lastPrice)} zł</TableCell>
            )}
            {columnInfo.find((x) => x.id === 'totalWeight')?.visible && (item.totalWeight || item.totalWeight === 0) && (
                <TableCell align="center">{priceFormatter(item.totalWeight)}</TableCell>
            )}

            <Dialog fullWidth open={dialogOpen} onClose={handelDialogOpen}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>{item.name}</Box>
                        <Box>
                            <Button variant="outlined" onClick={handelDialogOpen} sx={{ ...buttonStyle, ...simpleBtnStyle }}>
                                {getTitle(language, 'close')}
                            </Button>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            margin="dense"
                            id="name"
                            type="text"
                            label={getTitle(language, FIELDS.ITEMS.ITEM_NAME)}
                            placeholder={(modifiedItem !== undefined && modifiedItem.name) || item.name}
                            fullWidth
                            value={modifiedItem.name}
                            variant="standard"
                            onChange={handleChange}
                            sx={updateFormInput}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            inputRef={inputCountRef}
                            margin="dense"
                            id="count"
                            type="number"
                            label={getTitle(language, 'count')}
                            placeholder={getTitle(language, 'count')}
                            fullWidth
                            variant="standard"
                            value={newStock.count}
                            onChange={handleChange ?? ''}
                            onFocus={() => inputCountRef.current && inputCountRef.current.select()}
                            sx={updateFormInput}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            inputRef={inputPriceRef}
                            margin="dense"
                            id="price"
                            type="number"
                            label={getTitle(language, 'price')}
                            placeholder={getTitle(language, 'price')}
                            fullWidth
                            variant="standard"
                            value={newStock.price}
                            onChange={handleChange}
                            onFocus={() => inputPriceRef.current && inputPriceRef.current.select()}
                            sx={updateFormInput}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            inputRef={inputWOPRef}
                            margin="dense"
                            id="weightOfPiece"
                            type="number"
                            label={getTitle(language, 'weight_of_piece')}
                            placeholder={getTitle(language, 'weight_of_piece')}
                            fullWidth
                            variant="standard"
                            value={newStock.weightOfPiece}
                            onChange={handleChange}
                            onFocus={() => inputWOPRef.current && inputWOPRef.current.select()}
                            sx={updateFormInput}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button variant="outlined" onClick={handleDeleteItemDialog} endIcon={<DeleteIcon />} sx={{ ...buttonItemsStyle, ...simpleBtnStyle }} color="error">
                        {getTitle(language, 'delete')}
                    </Button>
                    <Button variant="outlined" onClick={handleSaveItem} endIcon={<SendIcon />} sx={{ ...buttonItemsStyle, ...simpleBtnStyle }} disabled={!disabledSaveButton}>
                        {getTitle(language, 'save')}
                    </Button>
                </DialogActions>
            </Dialog>
            {deleteDialogOpen && <DeleteDialog dialogOpen={deleteDialogOpen} handelDialogOpen={handleDeleteItemDialog} itemName={item.name} handleDelete={deleteItem} />}
            <MySnackBar
                duration={5000}
                openSnackBar={saveItemSuccessSnackBar}
                handleClose={() => setSaveItemSuccessSnackBar(false)}
                severity={'success'}
                alertText="Save item success!"
            />
        </Fragment>
    );
}

export default ItemCard;
